import React, { FC, useState, useCallback } from 'react';
import { Button } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/Common/UIKit/Input/Input';
import { StatusMessage } from '@/components/Common/CodeAuthenticator';
import { isMaskedEmail } from '@/components/utils';
import styles from './OTPAuthEmailBox.module.scss';
import { EMAIL_REGEXP } from '@/regExp';

interface OTPAuthEmailBoxProps {
    recipientEmail: string;
    onEmailSubmit: (email: string) => void;
    isLoading?: boolean;
    errorMessages?: StatusMessage[];
    textSource: 'phone' | 'email' | null;
    /**
     * Kept for call-site compatibility. Masked public meta is always a
     * placeholder only — Cognito needs a typed cleartext address; auth must
     * not demask bound recp_email on the unauthenticated OTP route (SPX-6446).
     */
    allowRecipientOverride?: boolean;
}

const OTPAuthEmailBox: FC<OTPAuthEmailBoxProps> = observer(({
    recipientEmail,
    onEmailSubmit,
    isLoading,
    errorMessages: _errorMessages = [],
    textSource,
}) => {
    const { t } = useTranslation();
    const nameSpace = 'recipientPage';
    // Bound share link: public meta returns masked recp_email — display only.
    const maskedHint = isMaskedEmail(recipientEmail) ? recipientEmail : '';
    const [email, setEmail] = useState(maskedHint ? '' : (recipientEmail || ''));
    const [error, setError] = useState(false);
    const [isDebounced, setIsDebounced] = useState(false);

    const handleSubmit = useCallback(() => {
        if (isDebounced) return;

        const trimmed = email.trim();
        const isValid = !!trimmed
            && !isMaskedEmail(trimmed)
            && !!trimmed.match(EMAIL_REGEXP);
        if (isValid) {
            setIsDebounced(true);
            setError(false);
            onEmailSubmit(trimmed);
            setTimeout(() => {
                setIsDebounced(false);
            }, 3000);
        } else {
            setError(true);
        }
    }, [email, isDebounced, onEmailSubmit]);

    return (
        <div className={styles.emailBoxContainer} data-testid="recipientPage_auth_emailBoxContainer">
            <h2 className={styles.title} data-testid="recipientPage_auth_emailTitle">
                {t(`${nameSpace}.OTPSteps.enterEmailTitle`)}
            </h2>
            <p className={styles.description} data-testid="recipientPage_auth_emailDescription">
                {textSource === 'phone'
                    ? t(`${nameSpace}.OTPSteps.enterEmailFromPhoneDescription`)
                    : t(`${nameSpace}.OTPSteps.enterEmailDescription`)}
            </p>
            <div className={styles.inputWrapper} data-testid="recipientPage_auth_emailInputWrapper">
                <Input
                    placeholder={
                        maskedHint
                        || t(`${nameSpace}.OTPSteps.emailPlaceholder`)
                    }
                    className={styles.emailInput}
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) {
                            setError(false);
                        }
                    }}
                    onPressEnter={handleSubmit}
                    status={error ? 'error' : undefined}
                    suffix={<MailOutlined className={styles.emailIcon} />}
                    data-testid="recipientPage_auth_emailInput"
                />
            </div>
            <Button
                type="primary"
                className={styles.sendButton}
                onClick={handleSubmit}
                loading={isLoading || isDebounced}
                disabled={isLoading || isDebounced}
                data-testid="recipientPage_auth_sendCodeButton"
            >
                {t(`${nameSpace}.OTPSteps.sendCode`)}
            </Button>
        </div>
    );
});

export default OTPAuthEmailBox;
